/**
 * "You are also…" — moving between the accounts one human holds.
 *
 * Used by all three shells (staff, patient portal, rep portal), because the
 * question is the same wherever you are standing and the answer comes from
 * whichever token you are holding.
 *
 * Switching does not widen the session. The server mints a NEW token through
 * the target audience's own login path and tells us where to put it and where
 * to go; this component swaps the storage key and navigates. The old token is
 * dropped, deliberately — holding two at once is what made "log out" ambiguous
 * in the first place.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { ChevronDown, Check, Loader2, ShieldAlert, Users } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || '';

const C = {
  border: 'var(--divider, #e5e7eb)',
  text: 'var(--text-primary, #111827)',
  muted: 'var(--text-secondary, #6b7280)',
  blue: '#2563eb', red: '#dc2626', amber: '#d97706',
};

const STORE = {
  'localStorage:token': ['localStorage', 'token'],
  'sessionStorage:portal_token': ['sessionStorage', 'portal_token'],
  'sessionStorage:rep_token': ['sessionStorage', 'rep_token'],
};

/** The token for the session we are currently in, whichever that is. */
function currentToken() {
  try {
    return sessionStorage.getItem('rep_token')
        || sessionStorage.getItem('portal_token')
        || localStorage.getItem('token');
  } catch { return null; }
}

export default function IdentitySwitcher({ compact = false, inline = false,
                                          onContext = null }) {
  const [me, setMe] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState('');
  // Only ever set for a staff target: it is the one switch that grants
  // authority over other people's records. `mode` is which proof that target
  // needs — a Google Sign-In account has no password its owner knows, so it
  // gets a one-time code to its own address instead.
  const [pwFor, setPwFor] = useState(null);
  const [pw, setPw] = useState('');
  const [mode, setMode] = useState('password');
  const [sentTo, setSentTo] = useState('');

  const load = useCallback(async () => {
    const tok = currentToken();
    if (!tok) return;
    try {
      const r = await fetch(`${API}/api/identity/me`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      if (r.ok) {
        const data = await r.json();
        setMe(data);
        // Hand the resolved identity back up. The staff header needs the
        // COMPANY name and cannot always resolve it itself — a client login
        // has no access to the companies endpoint, so `selectedCompany` stays
        // null and the dropdown showed a laboratory with no company above it.
        // This already knows the answer; reporting it avoids a second call.
        onContext?.(data.current || null);
      }
    } catch { /* a switcher that cannot load is simply not shown */ }
  }, [onContext]);
  useEffect(() => { load(); }, [load]);

  const go = async (target, secret) => {
    setBusy(`${target.kind}:${target.ref_id}`); setErr('');
    const asCode = (target.step_up_method === 'email_code');
    try {
      const r = await fetch(`${API}/api/identity/switch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json',
                   Authorization: `Bearer ${currentToken()}` },
        body: JSON.stringify({
          kind: target.kind, ref_id: target.ref_id,
          ...(asCode ? { code: secret || undefined }
                     : { password: secret || undefined }),
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        const d = data?.detail;
        if (d && d.code === 'PASSWORD_REQUIRED') {
          setMode('password'); setPwFor(target); setBusy(null); return;
        }
        if (d && d.code === 'CODE_SENT') {
          // Not an error: the code is on its way and we now need it.
          setMode('code'); setPwFor(target); setSentTo(d.message || '');
          setPw(''); setBusy(null); return;
        }
        setErr(typeof d === 'string' ? d : (d?.message || 'That switch was refused.'));
        setBusy(null); return;
      }
      const [store, key] = STORE[data.storage] || [];
      if (!store) { setErr('The server did not say where to keep that session.'); setBusy(null); return; }
      // Drop every other session before storing the new one. Carrying two at
      // once is exactly the ambiguity this feature exists to remove.
      try {
        localStorage.removeItem('token');
        sessionStorage.removeItem('portal_token');
        sessionStorage.removeItem('rep_token');
        (store === 'localStorage' ? localStorage : sessionStorage)
          .setItem(key, data.access_token);
      } catch { /* storage blocked — nothing further we can do */ }
      window.location.assign(data.redirect);
    } catch (e) {
      setErr(e.message || 'That switch could not be completed.');
      setBusy(null);
    }
  };

  const others = me?.other_identities || [];
  if (!me || others.length === 0) return null;   // most people are one thing

  return (
    <div style={{ position: 'relative', width: inline ? '100%' : undefined }}>
      <button onClick={() => setOpen(o => !o)}
        style={inline
          ? { display: 'flex', width: '100%', alignItems: 'center', gap: 8,
              padding: '10px 20px', border: 'none', background: 'transparent',
              color: C.text, cursor: 'pointer', fontSize: 13, fontWeight: 600,
              textAlign: 'left' }
          : { display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: compact ? '5px 10px' : '7px 12px', borderRadius: 8,
              border: `1px solid ${C.border}`, background: 'transparent',
              color: C.text, cursor: 'pointer', fontSize: 12.5, fontWeight: 600 }}>
        <Users size={inline ? 16 : 13} />
        <span style={{ flex: inline ? 1 : undefined }}>
          {inline ? `Switch account (${others.length})`
                  : (compact ? 'Switch' : (me.current?.label || 'Switch account'))}
        </span>
        <ChevronDown size={13} />
      </button>

      {open && (
        <>
          {/* The click-away shield is only for the FLOATING panel. Inline, the
              list is part of the menu it lives in and that menu closes itself;
              a full-screen shield there would swallow the first click on
              anything else in the dropdown. */}
          {inline ? null : (
            <div onClick={() => { setOpen(false); setPwFor(null); setErr(''); }}
                 style={{ position: 'fixed', inset: 0, zIndex: 9400 }} />
          )}
          {/* Inline: expand IN PLACE, at the width of whatever contains it.
              It used to float 320px wide anchored to the right edge, which
              inside a narrower dropdown pushed the panel off the left side and
              clipped every name. A menu inside a menu belongs in the flow. */}
          <div style={inline
            ? { position: 'static', width: '100%', marginTop: 2,
                background: 'transparent', color: C.text,
                borderTop: `1px solid ${C.border}`,
                borderBottom: `1px solid ${C.border}`, overflow: 'hidden' }
            : { position: 'absolute', right: 0, top: '100%', marginTop: 6,
                minWidth: 320, maxWidth: '92vw', zIndex: 9401,
                background: 'var(--card-bg-solid, #fff)', color: C.text,
                border: `1px solid ${C.border}`, borderRadius: 12,
                boxShadow: '0 18px 40px rgba(0,0,0,0.28)', overflow: 'hidden' }}>
            {inline ? null : (
              <div style={{ padding: '10px 14px', borderBottom: `1px solid ${C.border}` }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.muted,
                              textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  Signed in as
                </div>
                <div style={{ fontSize: 13.5, fontWeight: 700, marginTop: 2 }}>
                  {me.current?.full_name || me.current?.email}
                </div>
                <div style={{ fontSize: 12, color: C.muted }}>
                  {me.current?.label}
                  {me.current?.laboratory ? ` · ${me.current.laboratory}` : ''}
                </div>
              </div>
            )}

            {err ? (
              <div style={{ padding: '8px 14px', fontSize: 12, color: C.red,
                            borderBottom: `1px solid ${C.border}` }}>{err}</div>
            ) : null}

            {pwFor ? (
              <div style={{ padding: inline ? '12px 20px' : '12px 14px' }}>
                <div style={{ display: 'flex', gap: 7, alignItems: 'flex-start',
                              fontSize: 12, color: C.amber, marginBottom: 8 }}>
                  <ShieldAlert size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                  <span>
                    {mode === 'code' ? sentTo : (
                      <>
                        Re-enter the password for the <strong>{pwFor.label}</strong> account.
                        It is the one identity that opens other people&apos;s records.
                      </>
                    )}
                  </span>
                </div>
                <input type={mode === 'code' ? 'text' : 'password'}
                       inputMode={mode === 'code' ? 'numeric' : undefined}
                       placeholder={mode === 'code' ? '6-digit code' : undefined}
                       value={pw} autoFocus
                       onChange={e => setPw(e.target.value)}
                       onKeyDown={e => { if (e.key === 'Enter') go(pwFor, pw); }}
                       style={{ width: '100%', padding: '8px 10px', borderRadius: 7,
                                border: `1px solid ${C.border}`, background: 'transparent',
                                color: C.text, fontSize: 13, boxSizing: 'border-box' }} />
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end',
                              marginTop: 10 }}>
                  <button onClick={() => { setPwFor(null); setPw(''); setErr('');
                                           setMode('password'); setSentTo(''); }}
                    style={{ padding: '6px 12px', borderRadius: 7, fontSize: 12,
                             border: `1px solid ${C.border}`, background: 'transparent',
                             color: C.text, cursor: 'pointer' }}>Cancel</button>
                  <button onClick={() => go(pwFor, pw)} disabled={!pw}
                    style={{ padding: '6px 14px', borderRadius: 7, fontSize: 12,
                             fontWeight: 700, border: `1px solid ${C.blue}`,
                             background: C.blue, color: '#fff', cursor: 'pointer' }}>
                    Switch
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ maxHeight: 320, overflow: 'auto' }}>
                {others.map(o => {
                  const key = `${o.kind}:${o.ref_id}`;
                  const disabled = !o.is_active || o.blocked;
                  return (
                    <button key={key} disabled={disabled || busy === key}
                      onClick={() => {
                        if (!o.requires_password) { go(o); return; }
                        if (o.step_up_method === 'email_code') {
                          // Ask the server to send one; it comes back CODE_SENT.
                          go(o);
                        } else {
                          setMode('password'); setPwFor(o);
                        }
                      }}
                      style={{ display: 'flex', width: '100%', gap: 10,
                               alignItems: 'center', textAlign: 'left',
                               padding: inline ? '9px 20px' : '10px 14px',
                               background: 'transparent',
                               border: 'none', borderBottom: `1px solid ${C.border}`,
                               color: disabled ? C.muted : C.text,
                               cursor: disabled ? 'not-allowed' : 'pointer',
                               opacity: disabled ? 0.55 : 1, fontSize: 13 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600 }}>{o.label}</div>
                        <div style={{ fontSize: 11.5, color: C.muted,
                                      overflowWrap: 'anywhere' }}>
                          {o.laboratory || '—'}
                          {o.detail ? ` · ${o.detail}` : ''}
                          {o.blocked ? ' · blocked pending exclusion review' : ''}
                          {!o.is_active && !o.blocked ? ' · not active' : ''}
                        </div>
                      </div>
                      {busy === key ? <Loader2 size={14} className="spin" />
                        : o.requires_password ? <ShieldAlert size={14} color={C.amber} />
                        : <Check size={14} color={C.muted} />}
                    </button>
                  );
                })}
              </div>
            )}

            <div style={{ padding: inline ? '8px 20px' : '9px 14px',
                          fontSize: 11, color: C.muted }}>
              Switching signs you out of this one. Each account keeps its own
              password and sees only its own data.
            </div>
          </div>
        </>
      )}
    </div>
  );
}
